import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import React, { useEffect, useMemo, useState } from 'react';
import { Forms, InProgressDriving } from '@mummssoftware/utils/constants';
import styled from 'styled-components';
import { Observation } from '@mummssoftware/utils/api/Observation';
import { VisitDetail } from './VisitDetail';
import { Loader } from '../Loader';
import { detailUI, faceToFaceKey, travelTimeKey } from './constants';
import { defaults } from './en';
import { summaryMapper, patientHCPCSSummaryMapper } from './valueGetters';
import { Manifest } from './Links';
const FormsDiv = styled.div.withConfig({
  displayName: "VisitContents__FormsDiv",
  componentId: "sc-1b1bxzn-0"
})(["display:flex;flex-direction:row;flex-wrap:wrap;width:100%;max-width:min(100%,42rem);box-sizing:border-box;"]);

/** null / undefined / '' from the API are treated as absent. */
const hasPresentValue = value => value != null && value !== '';

const parseDetailValue = (rawValue, scalar) => {
  if (!hasPresentValue(rawValue)) return null;
  if (scalar) return rawValue;

  try {
    return JSON.parse(rawValue);
  } catch {
    return null;
  }
};
/** Driving with no minutes yet → Travel Time shows "In progress" via travelTimeMapper. */


const withTravelTimeDisplay = dto => {
  if ((dto === null || dto === void 0 ? void 0 : dto.status) !== InProgressDriving || hasPresentValue(dto === null || dto === void 0 ? void 0 : dto[travelTimeKey])) {
    return dto;
  }

  return _objectSpread(_objectSpread({}, dto), {}, {
    [travelTimeKey]: defaults.travelTimeInProgress
  });
};

const getAttsDetail = (atts, editing, editAttributes, summary) => atts && /*#__PURE__*/React.createElement(VisitDetail, {
  key: detailUI[0].key,
  mapper: summary ? summaryMapper : detailUI[0].mapper,
  title: detailUI[0].title,
  details: atts.filter(att => att.isselected),
  editing: editing,
  renderEditComponent: editAttributes,
  summary: summary
});

const getDetailRaw = (dto, field) => {
  if ((dto === null || dto === void 0 ? void 0 : dto[field]) != null && dto[field] !== '') return dto[field]; // Live/saved visit uses camelCase physicianServices (often already an array)

  if (field === 'physicianservices') {
    return dto === null || dto === void 0 ? void 0 : dto.physicianServices;
  }

  return undefined;
};

const getDetailUI = (dto, editing, editAttributes, summary) => detailUI.map(propNames => {
  const detailObj = parseDetailValue(getDetailRaw(dto, propNames.field), propNames.scalar);

  if (propNames.field === 'visitattrsumdto') {
    return getAttsDetail(detailObj, editing, editAttributes, summary);
  }

  let {
    mapper
  } = propNames;

  if (summary && !propNames.scalar) {
    mapper = propNames.field === 'physicianservices' ? patientHCPCSSummaryMapper : summaryMapper;
  }

  return detailObj != null && /*#__PURE__*/React.createElement(VisitDetail, {
    key: propNames.key,
    title: propNames.title,
    mapper: mapper,
    details: detailObj,
    summary: propNames.scalar ? false : summary
  });
});

const ManifestDetail = ({
  agency,
  keycloak,
  patientNumber,
  ManifestWidget,
  observation,
  dto,
  summary
}) => {
  const makeFormsStatusString = patientManifests => agency && keycloak && observation && patientNumber ? /*#__PURE__*/React.createElement(FormsDiv, null, patientManifests.map((form, i) => /*#__PURE__*/React.createElement(React.Fragment, {
    key: form.id || form.formname
  }, /*#__PURE__*/React.createElement(Manifest, {
    patientNumber: patientNumber,
    mfst: form,
    agency: agency,
    keycloak: keycloak,
    Widget: ManifestWidget,
    observation: observation
  }), i < patientManifests.length - 1 && ', '))) : `${patientManifests.map(form => `${form.formname}${form.status === defaults.submitted ? `` : ` (${form.status})`}`).join(', ')}`;

  return /*#__PURE__*/React.createElement(VisitDetail, {
    key: `${detailUI[1].key}-details-${Forms}`,
    mapper: summary ? summaryMapper : makeFormsStatusString,
    details: dto,
    title: defaults.forms,
    summary: summary
  });
};

var _ref = /*#__PURE__*/React.createElement(Loader, {
  contained: true
});

export const VisitContents = ({
  dto,
  // visitDetails
  editing,
  editAttributes,
  editF2F,
  summary,
  // show numbers instead of details
  // MANIFEST props
  agency,
  keycloak,
  patientNumber,
  ManifestWidget,
  observationUrl // optional, defaults to process.env value

}) => {
  const manifestDTO = parseDetailValue(dto === null || dto === void 0 ? void 0 : dto.formsdto);
  const observation = useMemo(() => agency && keycloak && new Observation({
    agency,
    keycloak,
    url: observationUrl || process.env.REACT_APP_OBSERVATION_URL
  }), [agency, keycloak, observationUrl]);
  const [showLoader, setShowLoader] = useState(true);
  useEffect(() => {
    setShowLoader(true);
    let timer;

    if (!dto) {
      timer = setTimeout(() => setShowLoader(false), 30000);
    }

    return () => {
      if (timer !== undefined) {
        clearTimeout(timer);
      }
    };
  }, [dto]);

  if (!dto) {
    return showLoader ? _ref : null;
  }

  return /*#__PURE__*/React.createElement(React.Fragment, null, getDetailUI(withTravelTimeDisplay(dto), editing, editAttributes, summary), (manifestDTO === null || manifestDTO === void 0 ? void 0 : manifestDTO[0]) && /*#__PURE__*/React.createElement(ManifestDetail, {
    agency: agency,
    keycloak: keycloak,
    observation: observation,
    patientNumber: patientNumber,
    ManifestWidget: ManifestWidget,
    dto: manifestDTO,
    summary: summary
  }), // EditFTF handles its own editing status, but we always render the checkbox
  ((dto === null || dto === void 0 ? void 0 : dto.isfacetofaceallowed) || (dto === null || dto === void 0 ? void 0 : dto.facetofacevisit)) && /*#__PURE__*/React.createElement(VisitDetail, {
    key: `${faceToFaceKey}-details`,
    title: defaults.F2F,
    editing: true,
    renderEditComponent: editF2F,
    summary: summary
  }));
};